<template>
	<div class="purchase-history">
		<TableFilters
			placeholder="Search by uuid..."
			:controls="controls"
			:filters="filters" />
		<div class="purchase-history__section">
			<h2 class="purchase-history__section-title">Purchase History</h2>
			<ApiTable
				flat
				:list-state="listState">
				<Column header="Purchase">
					<template #body="{ data }">
						<div class="purchase-history__purchase-container">
							<span>{{ format(data.created_at, 'LLL do yyyy') }}</span>
							<router-link
								class="purchase-history__uuid"
								:to="{
									name: props.routeName,
									params: props.routeParams,
									query: { tab: 'usage', s: data.stripe_purchase_id },
								}"
								>{{ data.stripe_purchase_id }}</router-link
							>
						</div>
					</template>
				</Column>
				<Column
					header="Token Batch"
					header-style="width: 200px">
					<template #body="{ data }">
						<router-link
							v-if="data.token_batch?.uuid"
							class="purchase-history__uuid"
							:to="{
								name: props.routeName,
								params: props.routeParams,
								query: { tab: 'usage', s: data.token_batch.uuid },
							}"
							>{{ data.token_batch.uuid }}</router-link
						>
					</template>
				</Column>
				<Column
					header="Amount"
					header-style="width: 200px">
					<template #body="slotProps">
						<span>{{ (Number(slotProps.data.amount) / 100).toFixed(2) }} €</span>
					</template>
				</Column>
			</ApiTable>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import useApiTable from '@/components/Table/useApiTable'
import TableFilters from '@/components/filters/TableFilters.vue'
import { usePurchaseListState } from '@/models/Purchase/States'
import { format } from 'date-fns'
import { FilterControls } from '@/helpers/filters/FilterControls'
import { Filters } from '@/helpers/filters/Filters'
import { useRoute } from 'vue-router'

const props = withDefaults(
	defineProps<{
		routeName?: string
		routeParams?: Record<string, string | number>
		userId?: number
	}>(),
	{
		routeName: 'account-details',
		routeParams: () => ({}),
		userId: undefined,
	},
)

const emit = defineEmits<{
	(e: 'start-loading'): void
	(e: 'stop-loading'): void
	(e: 'update:search', value: string): void
}>()

const route = useRoute()

const listLoading = ref(false)
const listState = usePurchaseListState()
const { ApiTable, Column } = useApiTable(listState)
listState.defaultParams.per_page = 10
listState.defaultParams.with = ['tokenBatch']
listState.query().where('status', '=', 'processed').save()

const { controls, filters } = useSearch()

watch(
	() => props.userId,
	(userId) => {
		if (userId) {
			listState.defaultParams.fromRelation = {
				model: 'App\\Models\\User',
				id: userId,
				relation: 'purchases',
			}
		} else {
			delete listState.defaultParams.fromRelation
		}
		refresh()
	},
	{ immediate: true },
)

async function refresh() {
	listLoading.value = true
	emit('start-loading')
	await listState.getList()
	listLoading.value = false
	emit('stop-loading')
}

function useSearch() {
	const uuidSearch = ref<string>('')
	const controls = new FilterControls([])
	const filters = ref(Filters.fromQuery(route.query as any, controls))

	watch(
		() => route.query.s,
		async (val) => {
			filters.value.search = typeof val === 'string' ? val : ''
			uuidSearch.value = filters.value.search
		},
		{ immediate: true },
	)

	watch(
		() => filters.value.search,
		async (val, prev) => {
			if (val === prev) return
			emit('update:search', val)

			const uuid = typeof val === 'string' ? val : undefined
			if (!uuid) {
				listState.query().where('status', '=', 'processed').save()
				listState.getList()
				return
			}
			listState
				.query()
				.where('status', '=', 'processed')
				.where('stripe_purchase_id', '=', uuid)
				.save()
			refresh()
		},
		{
			immediate: true,
		},
	)

	return {
		controls,
		filters,
	}
}
</script>

<style scoped lang="scss">
.purchase-history {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 16px;

	.purchase-history__purchase-container {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.purchase-history__uuid {
		color: var(--text-color);
		opacity: 0.8;
		text-decoration: none;
		text-wrap: nowrap;
		font-family: monospace;

		&:hover {
			text-decoration: underline;
		}

		&--total {
			font-weight: 700;

			&:hover {
				text-decoration: none;
			}
		}
	}

	.purchase-history__section {
		display: flex;
		flex-direction: column;
		gap: 16px;

		.purchase-history__section-title {
			font-size: 22px;
			font-weight: 800;
		}
	}
}
</style>
